// Was der Verkäufer tut, landet sofort in der Arbeitskopie am Gerät – und
// in derselben Transaktion im Postausgang. Wird die App mittendrin
// geschlossen, ist beides da oder beides nicht; nie das eine ohne das andere.
//
// Alle Kennungen entstehen hier am Gerät (Plan E2, „Ich entscheide“): Ohne
// Empfang muss schon alles seinen festen Namen haben.
//
// Seit E2, Schritt 4: „An das Büro senden“ (`abschliessen`) schließt die
// Besichtigung ab. Danach ändert keine Funktion hier mehr etwas an ihr –
// außer über „Etwas nachreichen“ (seit Schritt 5). Was geht, entscheidet
// `sendungPruefen` in jeder Transaktion, an einer Stelle: Wird
// aufgenommen, gehört die Änderung zur ersten Sendung; läuft ein Nachtrag,
// zu ihm – dann kommt Neues dazu, Längen und „Sonstiges“ lassen sich
// berichtigen, und ändern oder löschen lässt sich nur, was mit ihm dazukam
// (dieselben Regeln wie am Server, eintraege.ts). Sonst `SchonGesendet`.
// Ausnahme: die Dachplanung mit K2, sie darf jederzeit nachkommen.
//
// Seit E3b (O65): Material von draußen (`materialDazu`, `materialMenge`,
// `materialEntfernen`) – nach den Regeln der Längen: neu dazu, im Nachtrag
// die Menge eines gesendeten berichtigen, entfernen nur, was in derselben
// Sendung dazukam. Den Text einer eigenen Angabe ändern (`materialAendern`)
// geht, wo auch das Entfernen ginge.
//
// Seit E3c (S5): Höhe und Zugang am Dach (`dachFaktenSpeichern`) – nach den
// Regeln von „Sonstiges“, auch im Nachtrag; und an der Dachplanung mit K2 der
// zweite Platz für den Excel-Report (`k2ExcelAblegen`), der wie das PDF
// jederzeit nachkommen darf.

import {
  AUFNAHME_GRENZEN,
  dachFaktenDa,
  dachFaktenGleich,
  namensVorschlag,
  STATIONEN,
  neueKennung,
  vollerName,
  type DachFakten,
  type MemoFormat,
  type Zeichnung,
} from "@happy-energy/kern";
import {
  speicher,
  stueckeTabellen,
  type Aufnahme,
  type Foto,
  type K2,
  type K2Excel,
  type K2Teil,
  type Laenge,
  type Materialangabe,
  type Memo,
  type Nachtrag,
  type Notiz,
  type Station,
} from "./speicher.ts";
import {
  aufnahmeFehlerAktualisieren,
  austragen,
  hintenAnstellen,
  nachtragLeer,
  postausgangTabellen,
  stueckeLesen,
  vormerken,
  type VormerkenZusatz,
} from "./postausgang.ts";
import { pruefsummeBerechnen, type FotoBereit } from "./foto.ts";
import { dateiAblegen, dateiWegWennUnbenutzt } from "./dateien.ts";
import { besichtigungWegraeumen, wegraeumTabellen } from "./wegraeumen.ts";

// --- Was sich bearbeiten lässt (seit E2, Schritt 4 und 5) --------------------------------

/**
 * Das geht nicht mehr: Die Besichtigung ist schon abgeschlossen – ändern
 * geht nur über „Etwas nachreichen“. Im Nachtrag auch: etwas, das schon
 * gesendet ist (dann mit eigener Meldung). Kein Fehler der App – die
 * Oberfläche sagt die Meldung, die Fehlerwache erfährt nichts.
 */
export class SchonGesendet extends Error {
  constructor(meldung = "Schon gesendet – ändern geht über „Etwas nachreichen“.") {
    super(meldung);
    this.name = "SchonGesendet";
  }
}

/**
 * Im Nachtrag eine gesendete Länge leeren: Das hieße, sie am Server zu
 * löschen – und was gesendet ist, bleibt. Wer sich vertan hat, trägt 0 ein.
 */
export class LeerImNachtrag extends SchonGesendet {
  constructor() {
    super("Im Nachtrag geht leer nicht – trag 0 ein, wenn es keine gibt.");
    this.name = "LeerImNachtrag";
  }
}

/**
 * Wie sich eine Besichtigung bearbeiten lässt – die eine Regel für
 * Arbeitskopie und Oberfläche:
 *  - aufnahme: Sie wird aufgenommen – alles lässt sich ändern.
 *  - nachtrag: Sie ist im Büro, und „Etwas nachreichen“ läuft – Neues kommt
 *    mit diesem Nachtrag dazu, Längen und „Sonstiges“ lassen sich
 *    berichtigen; was schon gesendet ist, bleibt.
 *  - gesperrt: gesendet oder auf dem Weg dorthin (auch ein Nachtrag, der
 *    gerade gesendet wird) – nur zum Ansehen.
 */
export type Bearbeiten = { art: "aufnahme" } | { art: "nachtrag"; nachtragId: string } | { art: "gesperrt" };

/** Wird die Besichtigung noch aufgenommen? Nur dann ist sie ganz offen – Name und Adresse, „fertig“, Abschließen. */
export function aenderbar(a: Pick<Aufnahme, "stand">): boolean {
  return a.stand === "in_aufnahme";
}

/** Der Nachtrag, der gerade läuft – undefined, wenn keiner offen ist. */
export function offenerNachtrag<N extends Pick<Nachtrag, "stand">>(nachtraege: readonly N[]): N | undefined {
  return nachtraege.find((n) => n.stand === "offen");
}

/** Der Nachtrag, der gerade gesendet wird („Nachtrag senden“ getippt, noch nicht bestätigt) – undefined, wenn keiner. */
export function nachtragUnterwegs<N extends Pick<Nachtrag, "stand">>(nachtraege: readonly N[]): N | undefined {
  return nachtraege.find((n) => n.stand === "abgeschlossen");
}

/** Wie sich die Besichtigung bearbeiten lässt – aus ihrem Stand und ihren Nachträgen. */
export function bearbeiten(a: Pick<Aufnahme, "stand">, nachtraege: readonly Pick<Nachtrag, "id" | "stand">[]): Bearbeiten {
  if (aenderbar(a)) return { art: "aufnahme" };
  const offen = a.stand === "im_buero" ? offenerNachtrag(nachtraege) : undefined;
  return offen ? { art: "nachtrag", nachtragId: offen.id } : { art: "gesperrt" };
}

/**
 * Die Tabellen, die jede schreibende Transaktion hier mitnimmt: den
 * Postausgang (mit den Aufnahmen) und die Nachträge – `sendungPruefen` liest sie.
 */
function schreibTabellen() {
  return [...postausgangTabellen(), speicher.nachtraege] as const;
}

/**
 * In der Transaktion jeder schreibenden Funktion: Zu welcher Sendung gehört
 * die Änderung? null – zur ersten (es wird aufgenommen); sonst die Kennung
 * des offenen Nachtrags. Lässt sich nichts mehr ändern: `SchonGesendet`.
 * Die Transaktion muss `schreibTabellen()` mitnehmen. Gibt es die
 * Besichtigung (noch) nicht, entscheidet die Funktion selbst, wie bisher.
 */
async function sendungPruefen(aufnahmeId: string): Promise<string | null> {
  const a = await speicher.aufnahmen.get(aufnahmeId);
  if (!a) return null;
  const b = bearbeiten(a, await speicher.nachtraege.where("aufnahmeId").equals(aufnahmeId).toArray());
  if (b.art === "gesperrt") throw new SchonGesendet();
  return b.art === "nachtrag" ? b.nachtragId : null;
}

/**
 * Ändern oder löschen lässt sich nur, was in derselben Sendung dazukam –
 * im Nachtrag also nichts, was schon gesendet ist (wie am Server,
 * nurAusDerselbenSendung). Beim Aufnehmen gehört alles zur ersten Sendung.
 */
function nurAusDerSendung(x: { nachtragId: string | null }, sendung: string | null, meldung: string): void {
  if ((x.nachtragId ?? null) !== sendung) throw new SchonGesendet(meldung);
}

/** Was beim Vormerken mitgeht: der Nachtrag, wenn die Änderung zu einem gehört. */
function mitSendung(sendung: string | null): VormerkenZusatz {
  return sendung ? { nachtragId: sendung } : {};
}

const FOTO_GESENDET = "Dieses Foto ist schon im Büro – es bleibt, wie es ist.";
const MEMO_GESENDET = "Dieses Sprachmemo ist schon im Büro – es bleibt, wie es ist.";

export interface NeueBesichtigung {
  /**
   * Seit E3b (O82) Vor- und Nachname – daraus wird `name` (Kern, vollerName).
   * Ohne Nachname gilt `name`, wie vorher (so legen die Tests von früher an).
   */
  kunde: { id: string; name?: string; vorname?: string; nachname?: string; telefon: string; email: string };
  objekt: { id: string; strasse: string; plz: string; ort: string };
  anlage: { id: string };
  aufnahmeId: string;
  /** Die Kennungen der sieben Stationen, in der Reihenfolge von STATIONEN. */
  stationIds: string[];
}

/** Die Kennungen einer neuen Besichtigung – einmal, wenn das Formular aufgeht. */
export function neueKennungen(): Pick<NeueBesichtigung, "aufnahmeId" | "stationIds"> & { kundeId: string; objektId: string; anlageId: string } {
  return {
    aufnahmeId: neueKennung(),
    kundeId: neueKennung(),
    objektId: neueKennung(),
    anlageId: neueKennung(),
    stationIds: STATIONEN.map(() => neueKennung()),
  };
}

const leerZuNull = (s: string): string | null => (s.trim() === "" ? null : s.trim());

/** „Besichtigung starten“: Kunde, Objekt, Anlage, Aufnahme und die sieben Stationen auf einmal. */
/** Name, Vorname und Nachname am Gerät: mit Nachname zusammengesetzt (Kern), sonst nur der Name, ungeteilt. */
function namenAmGeraet(k: { name?: string; vorname?: string; nachname?: string }): Pick<Aufnahme["kunde"], "name" | "vorname" | "nachname"> {
  const nachname = k.nachname?.trim() ?? "";
  if (!nachname) return { name: (k.name ?? "").trim() };
  const vorname = k.vorname?.trim() || null;
  return { name: vollerName(vorname, nachname), vorname, nachname };
}

export async function besichtigungAnlegen(e: NeueBesichtigung, jetzt = new Date()): Promise<string> {
  const aufnahme: Aufnahme = {
    id: e.aufnahmeId,
    kunde: { id: e.kunde.id, art: "person", ...namenAmGeraet(e.kunde), telefon: leerZuNull(e.kunde.telefon), email: leerZuNull(e.kunde.email) },
    objekt: { id: e.objekt.id, art: "einfamilienhaus", strasse: e.objekt.strasse.trim(), plz: e.objekt.plz.trim(), ort: e.objekt.ort.trim() },
    anlage: { id: e.anlage.id, produktlinie: "sun" },
    begonnenAm: jetzt.toISOString(),
    stand: "in_aufnahme",
    fehler: null,
    abgeschlossenAm: null,
    imBueroAm: null,
  };
  const stationen: Station[] = STATIONEN.map((s, i) => ({
    id: e.stationIds[i]!,
    aufnahmeId: e.aufnahmeId,
    art: s.schluessel,
    nr: s.nr,
    fertig: false,
    sonstiges: null,
    geaendertImNachtrag: null,
  }));
  await speicher.transaction("rw", [speicher.stationen, ...postausgangTabellen()], async () => {
    await speicher.aufnahmen.add(aufnahme);
    await speicher.stationen.bulkAdd(stationen);
    await vormerken("aufnahme", aufnahme.id, aufnahme.id);
  });
  return aufnahme.id;
}

/** Was im Formular zu Kunde und Objekt steht – so, wie getippt. Seit E3b Vor- und Nachname statt eines Namens (O82). */
export interface KundeAngaben {
  vorname: string;
  nachname: string;
  telefon: string;
  email: string;
  strasse: string;
  plz: string;
  ort: string;
}

/**
 * Die Angaben einer Besichtigung zum Vorbefüllen des Formulars. Ist der Name
 * (noch) nicht aufgeteilt – eine Besichtigung von vor E3b –, steht der
 * Vorschlag aus dem Kern da (letztes Wort = Nachname).
 */
export function kundeAngaben(a: Pick<Aufnahme, "kunde" | "objekt">): KundeAngaben {
  const namen = a.kunde.nachname ? { vorname: a.kunde.vorname ?? "", nachname: a.kunde.nachname } : namensVorschlag(a.kunde.name);
  return {
    ...namen,
    telefon: a.kunde.telefon ?? "",
    email: a.kunde.email ?? "",
    strasse: a.objekt.strasse,
    plz: a.objekt.plz,
    ort: a.objekt.ort,
  };
}

/**
 * „Besichtigung verwerfen“ (seit E3c, S6; PROJEKT.md, O97): eine
 * Besichtigung, die noch aufgenommen wird – also nie gesendet war –, ganz
 * vom Gerät nehmen (wegraeumen.ts) und dem Server „verwerfen“ schicken. Der
 * Eintrag steht allein im Postausgang: Was für die Besichtigung noch wartete
 * (auch der Eintrag „aufnahme“), ist mit ihr weg – nichts geht mehr hinaus,
 * was sie am Server anlegen könnte. Ohne Netz wartet „verwerfen“ wie alles
 * andere; am Server löscht es sie samt Anlage, Kunde und Objekt nach
 * derselben Regel wie das Büro – war sie dort nie angekommen, ist auch das
 * ein „ok“. „gesperrt“: Sie ist schon abgeschlossen oder fehlt – löschen
 * kann sie dann nur das Büro.
 *
 * Seit der Code-Prüfung E3c (Befund 3): Arbeitet das Büro schon mit ihrer
 * Anlage, löscht der Server nichts („buero-arbeitet“) – am Gerät bleibt sie
 * trotzdem weg, und die App sagt es einmal (sender.ts). Dafür trägt der
 * Eintrag Name und Adresse mit; sie bleiben am Gerät.
 */
export async function besichtigungVerwerfen(aufnahmeId: string): Promise<"verworfen" | "gesperrt"> {
  return speicher.transaction("rw", wegraeumTabellen(), async () => {
    const a = await speicher.aufnahmen.get(aufnahmeId);
    if (!a || !aenderbar(a)) return "gesperrt";
    await besichtigungWegraeumen(aufnahmeId);
    await vormerken("verwerfen", aufnahmeId, aufnahmeId, { verworfen: { name: a.kunde.name, ort: `${a.objekt.strasse}, ${a.objekt.ort}` } });
    return "verworfen";
  });
}

/**
 * Darf man Name und Adresse noch berichtigen? Nur, solange aufgenommen wird
 * – danach ist alles beim Büro, auch in einem Nachtrag nicht (Kunde und
 * Adresse berichtigt der Server dort nicht; das Büro, E3).
 */
export function kundeAenderbar(a: Pick<Aufnahme, "stand">): boolean {
  return aenderbar(a);
}

/**
 * Name und Adresse nach dem Start berichtigen (seit 19.10.2026, Augentest
 * am iPhone). Die Kennungen bleiben; der Eintrag „aufnahme“ wird neu
 * vorgemerkt – der Server ändert daran Name, Telefon, E-Mail, Straße, PLZ
 * und Ort (eintraege.ts, aufnahmeAnnehmen). Wartet er noch, behält er
 * seinen Platz vor den Stationen. Gibt zurück, was geschah:
 * „gesperrt“, wenn die Besichtigung schon abgeschlossen ist (oder fehlt).
 */
export async function kundeAendern(aufnahmeId: string, e: KundeAngaben): Promise<"geaendert" | "unveraendert" | "gesperrt"> {
  return speicher.transaction("rw", [...postausgangTabellen()], async () => {
    const a = await speicher.aufnahmen.get(aufnahmeId);
    if (!a || !kundeAenderbar(a)) return "gesperrt";
    // Ein ungeteilter Name bleibt ungeteilt, solange der Vorschlag unberührt ist – nichts wird still umgeschrieben (O82).
    const vorschlag = a.kunde.nachname ? null : namensVorschlag(a.kunde.name);
    const unberuehrt = vorschlag !== null && vorschlag.vorname === e.vorname.trim() && vorschlag.nachname === e.nachname.trim();
    const namen = unberuehrt ? { name: a.kunde.name } : namenAmGeraet(e);
    const { vorname: _v, nachname: _n, ...ohneNamen } = a.kunde;
    const kunde: Aufnahme["kunde"] = { ...ohneNamen, ...namen, telefon: leerZuNull(e.telefon), email: leerZuNull(e.email) };
    const objekt: Aufnahme["objekt"] = { ...a.objekt, strasse: e.strasse.trim(), plz: e.plz.trim(), ort: e.ort.trim() };
    if (JSON.stringify(kunde) === JSON.stringify(a.kunde) && JSON.stringify(objekt) === JSON.stringify(a.objekt)) return "unveraendert";
    await speicher.aufnahmen.update(aufnahmeId, { kunde, objekt });
    await vormerken("aufnahme", aufnahmeId, aufnahmeId);
    return "geaendert";
  });
}

/**
 * „Sonstiges“ einer Station – gespeichert, wie getippt; leer gilt als nicht
 * angegeben. Im Nachtrag (seit Schritt 5) heißt das „berichtigen“: Die
 * Station ist danach in diesem Nachtrag berichtigt (`geaendertImNachtrag`),
 * „fertig“ bleibt. Markiert und vorgemerkt wird sie nur, wenn sich der Text
 * wirklich ändert – derselbe Text noch einmal berichtigt nichts (der
 * Server markiert jede Station, zu der im Nachtrag ein Eintrag kommt).
 */
export async function sonstigesSpeichern(station: Pick<Station, "id" | "aufnahmeId">, text: string): Promise<void> {
  await speicher.transaction("rw", [speicher.stationen, ...schreibTabellen()], async () => {
    const sendung = await sendungPruefen(station.aufnahmeId);
    const sonstiges = text === "" ? null : text;
    if (sendung === null) {
      await speicher.stationen.update(station.id, { sonstiges });
      await vormerken("station", station.id, station.aufnahmeId);
      return;
    }
    const s = await speicher.stationen.get(station.id);
    if (!s || (s.sonstiges === sonstiges && s.geaendertImNachtrag !== sendung)) return;
    await speicher.stationen.update(station.id, { sonstiges, geaendertImNachtrag: sendung });
    await vormerken("station", station.id, station.aufnahmeId, mitSendung(sendung));
  });
}

/**
 * Höhe und Zugang am Dach (seit E3c, S5; ANFORDERUNGEN.md H32) – nur an der
 * Station „dach“, freiwillig. Nach den Regeln von „Sonstiges“: beim Aufnehmen
 * gespeichert und vorgemerkt, im Nachtrag berichtigt und mit dem Nachtrag
 * markiert (`geaendertImNachtrag`, dieselbe Marke) – vorgemerkt nur, wenn
 * sich wirklich etwas ändert. Nichts angegeben steht als null da. Die Werte
 * kommen geprüft (Kern, dachFaktenVertrag – die Seite prüft beim Tippen).
 */
export async function dachFaktenSpeichern(station: Pick<Station, "id" | "aufnahmeId">, fakten: DachFakten): Promise<void> {
  await speicher.transaction("rw", [speicher.stationen, ...schreibTabellen()], async () => {
    const sendung = await sendungPruefen(station.aufnahmeId);
    const s = await speicher.stationen.get(station.id);
    if (!s) return;
    if (s.art !== "dach") throw new Error("Höhe und Zugang gibt es nur an der Station Dach.");
    const fachfragen = dachFaktenDa(fakten) ? { ...fakten } : null;
    const gleich = s.fachfragen !== undefined && dachFaktenGleich(s.fachfragen, fachfragen);
    if (sendung === null) {
      if (gleich) return;
      await speicher.stationen.update(station.id, { fachfragen });
      await vormerken("station", station.id, station.aufnahmeId);
      return;
    }
    if (gleich && s.geaendertImNachtrag !== sendung) return;
    await speicher.stationen.update(station.id, { fachfragen, geaendertImNachtrag: sendung });
    await vormerken("station", station.id, station.aufnahmeId, mitSendung(sendung));
  });
}

/** „Station fertig“ – nur beim Aufnehmen: Im Nachtrag bleibt „fertig“, wie es gesendet wurde. */
export async function stationFertig(station: Pick<Station, "id" | "aufnahmeId">, fertig = true): Promise<void> {
  await speicher.transaction("rw", [speicher.stationen, ...schreibTabellen()], async () => {
    if ((await sendungPruefen(station.aufnahmeId)) !== null) throw new SchonGesendet("„Fertig“ bleibt im Nachtrag, wie es gesendet wurde.");
    const s = await speicher.stationen.get(station.id);
    if (!s || s.fertig === fertig) return;
    await speicher.stationen.update(station.id, { fertig });
    await vormerken("station", station.id, station.aufnahmeId);
  });
}

/**
 * Die Notiz zur ganzen Station – eine je Station und Sendung, ihre Kennung
 * entsteht beim ersten Buchstaben und bleibt. Wird sie geleert (und hat
 * kein Sprachmemo), geht „geloescht“ hinaus; wird sie wieder gefüllt,
 * wieder die Notiz. Es geht immer nur das eine von beiden hinaus, in der
 * richtigen Reihenfolge.
 *
 * Im Nachtrag (seit Schritt 5) ist das der „Nachtrag zur Notiz“: eine
 * eigene Notiz dieses Nachtrags an der Station – die gesendete bleibt, wie
 * sie ist (Gernot: Notizen kommen dazu, sie werden nicht berichtigt).
 */
export async function notizSpeichern(station: Pick<Station, "id" | "aufnahmeId">, text: string): Promise<void> {
  await notizAnSpeichern({ aufnahmeId: station.aufnahmeId, stationId: station.id, fotoId: null }, text);
}

/**
 * Die Notiz zu einem Foto – eine je Foto, sonst genau wie die Notiz zur
 * Station. Sie gehört zur Sendung ihres Fotos: An ein schon gesendetes
 * Foto kommt im Nachtrag keine mehr (`SchonGesendet`, wie am Server).
 */
export async function fotoNotizSpeichern(foto: Pick<Foto, "id" | "aufnahmeId">, text: string): Promise<void> {
  await notizAnSpeichern({ aufnahmeId: foto.aufnahmeId, stationId: null, fotoId: foto.id }, text);
}

/** Eine Notiz gehört genau zu einer Station oder einem Foto. */
export interface NotizOrt {
  aufnahmeId: string;
  stationId: string | null;
  fotoId: string | null;
}

/**
 * Die Notiz an diesem Ort, wenn es sie schon gibt – je Foto eine, je
 * Station eine je Sendung (`sendung`: null die erste, sonst der Nachtrag).
 */
function notizFinden(ort: NotizOrt, sendung: string | null): Promise<Notiz | undefined> {
  return ort.fotoId !== null
    ? speicher.notizen.where("fotoId").equals(ort.fotoId).first()
    : speicher.notizen
        .where("stationId")
        .equals(ort.stationId ?? "")
        .filter((n) => (n.nachtragId ?? null) === sendung)
        .first();
}

/**
 * Gibt es das Foto der Notiz noch, und gehört es zur Sendung? false: Es ist
 * inzwischen gelöscht – dann gibt es auch keine Notiz mehr dazu. Ein schon
 * gesendetes Foto im Nachtrag: `SchonGesendet`. Ohne Foto (Notiz zur
 * Station): true.
 */
async function fotoDerNotiz(ort: NotizOrt, sendung: string | null): Promise<boolean> {
  if (ort.fotoId === null) return true;
  const f = await speicher.fotos.get(ort.fotoId);
  if (!f) return false;
  nurAusDerSendung(f, sendung, FOTO_GESENDET);
  return true;
}

async function notizAnSpeichern(ort: NotizOrt, text: string): Promise<void> {
  await speicher.transaction("rw", [speicher.notizen, speicher.memos, speicher.fotos, ...schreibTabellen()], async () => {
    const sendung = await sendungPruefen(ort.aufnahmeId);
    if (!(await fotoDerNotiz(ort, sendung))) return;
    let n = await notizFinden(ort, sendung);
    if (!n) {
      if (text.trim() === "") return;
      n = { id: neueKennung(), aufnahmeId: ort.aufnahmeId, stationId: ort.stationId, fotoId: ort.fotoId, text, nachtragId: sendung };
      await speicher.notizen.add(n);
    } else {
      if (n.text === text) return;
      await speicher.notizen.update(n.id, { text });
    }
    const leer = text.trim() === "" && (await speicher.memos.where("notizId").equals(n.id).count()) === 0;
    if (leer) {
      await austragen("notiz", n.id);
      await vormerken("geloescht", n.id, ort.aufnahmeId, { ...mitSendung(sendung), tabelle: "notiz" });
    } else {
      await austragen("geloescht", n.id);
      await vormerken("notiz", n.id, ort.aufnahmeId, mitSendung(sendung));
    }
  });
}

// --- Fotos (seit E2, Schritt 2) ----------------------------------------------------

/**
 * Ein neues Foto an der Station – hinten angehängt. Erst die Datei, dann
 * das Foto in den Postausgang: So steht die Datei in der Reihe vor dem
 * Foto, das sie braucht. Gibt die Kennung des Fotos zurück. Im Nachtrag
 * (seit Schritt 5) kommt es mit ihm dazu und trägt seine Kennung.
 */
export async function fotoHinzufuegen(station: Pick<Station, "id" | "aufnahmeId">, bereit: FotoBereit): Promise<string> {
  const id = neueKennung();
  // Die Bytes vorher lesen – in der Transaktion darf nichts anderes warten als der Speicher.
  const daten = await bereit.datei.arrayBuffer();
  await speicher.transaction("rw", [speicher.fotos, speicher.dateien, ...schreibTabellen()], async () => {
    const sendung = await sendungPruefen(station.aufnahmeId);
    const vorhanden = await speicher.fotos.where("stationId").equals(station.id).toArray();
    const foto: Foto = {
      id,
      aufnahmeId: station.aufnahmeId,
      stationId: station.id,
      reihenfolge: vorhanden.reduce((hoechste, f) => Math.max(hoechste, f.reihenfolge + 1), 0),
      pruefsumme: bereit.pruefsumme,
      breite: bereit.breite,
      hoehe: bereit.hoehe,
      groesse: bereit.groesse,
      aufnahmezeit: bereit.aufnahmezeit,
      zeichnung: null,
      nachtragId: sendung,
      hochgeladen: false,
    };
    await dateiAblegen(bereit.pruefsumme, daten, bereit.datei.type || "image/jpeg");
    await speicher.fotos.add(foto);
    // Die Datei hängt an keiner Sendung – sie geht über den Datei-Eingang.
    await vormerken("datei", bereit.pruefsumme, station.aufnahmeId);
    await vormerken("foto", id, station.aufnahmeId, mitSendung(sendung));
  });
  return id;
}

/** Die Zeichnung auf einem Foto – ohne Striche gilt: keine Zeichnung. Im Nachtrag nur auf seinen eigenen Fotos. */
export async function zeichnungSpeichern(foto: Pick<Foto, "id" | "aufnahmeId">, zeichnung: Zeichnung | null): Promise<void> {
  const neu = zeichnung && zeichnung.striche.length > 0 ? zeichnung : null;
  await speicher.transaction("rw", [speicher.fotos, ...schreibTabellen()], async () => {
    const sendung = await sendungPruefen(foto.aufnahmeId);
    const f = await speicher.fotos.get(foto.id);
    if (!f) return;
    nurAusDerSendung(f, sendung, FOTO_GESENDET);
    if (JSON.stringify(f.zeichnung) === JSON.stringify(neu)) return;
    await speicher.fotos.update(foto.id, { zeichnung: neu });
    await vormerken("foto", foto.id, foto.aufnahmeId, mitSendung(sendung));
  });
}

/**
 * Ein Foto löschen – samt seiner Notiz (und deren Memos).
 *  - Ging es nie an den Server, verschwindet es nur am Gerät, mit allem,
 *    was dazu im Postausgang wartet (Datei, Foto, Foto-Notiz).
 *  - Sonst geht „geloescht“ hinaus; der Server löscht Foto und Notiz mit.
 * Die Datei bleibt im Postausgang, wenn ein anderes Foto dieselbe hat. Im
 * Nachtrag lassen sich nur seine eigenen Fotos löschen – was gesendet ist,
 * bleibt.
 */
export async function fotoLoeschen(foto: Pick<Foto, "id" | "aufnahmeId">): Promise<void> {
  const tabellen = [speicher.fotos, speicher.notizen, speicher.memos, speicher.memoTeile, speicher.dateien, ...schreibTabellen()];
  await speicher.transaction("rw", tabellen, async () => {
    const sendung = await sendungPruefen(foto.aufnahmeId);
    const f = await speicher.fotos.get(foto.id);
    if (!f) return;
    nurAusDerSendung(f, sendung, FOTO_GESENDET);
    for (const n of await speicher.notizen.where("fotoId").equals(f.id).toArray()) {
      // Die Memos gehen am Server mit dem Foto – hier nur am Gerät weg, samt ihren Dateien im Postausgang.
      for (const m of await speicher.memos.where("notizId").equals(n.id).toArray()) await memoAmGeraetEntfernen(m);
      await austragen("notiz", n.id);
      await austragen("geloescht", n.id);
      await speicher.notizen.delete(n.id);
    }
    await austragen("foto", f.id);
    await speicher.fotos.delete(f.id);
    if ((await speicher.fotos.where("pruefsumme").equals(f.pruefsumme).count()) === 0) await austragen("datei", f.pruefsumme);
    await dateiWegWennUnbenutzt(f.pruefsumme);
    if (f.abgeschickt) await vormerken("geloescht", f.id, f.aufnahmeId, { ...mitSendung(sendung), tabelle: "foto" });
  });
}

// --- Sprachmemos (seit E2, Schritt 3) ------------------------------------------------
//
// Ein Memo hängt an einer Notiz – gibt es noch keine, entsteht sie mit
// leerem Text. Während der Aufnahme liegt das Memo schon am Gerät, aber
// ohne Datei (Prüfsumme null); die Teilstücke kommen jede Sekunde dazu
// (memo.ts). Erst beim Stoppen wird daraus EINE Datei mit Prüfsumme, und
// erst dann geht etwas in den Postausgang – in der Reihenfolge Notiz, Datei,
// Memo. Bricht die Aufnahme ab (Absturz, App geschlossen, Akku leer), macht
// `memosRetten` beim nächsten Start aus den Teilstücken das Memo.

/** Die Dauer, wie sie gesendet wird: auf Zehntelsekunden, höchstens eine Stunde (Vertrag). */
function dauerRunden(sekunden: number): number {
  const s = Number.isFinite(sekunden) ? sekunden : 0;
  return Math.round(Math.min(AUFNAHME_GRENZEN.dauerSekunden, Math.max(0, s)) * 10) / 10;
}

/**
 * Eine Aufnahme beginnt: das Memo – noch ohne Datei – an der Notiz dieses
 * Ortes; gibt es noch keine Notiz, entsteht sie mit leerem Text. In den
 * Postausgang geht noch nichts. Gibt die Kennung des Memos zurück – null,
 * wenn das Foto inzwischen gelöscht ist. Im Nachtrag (seit Schritt 5)
 * gehören Memo und Notiz zu ihm: an der Station die Notiz dieses Nachtrags,
 * an einem Foto nur an einem, das mit ihm dazukam.
 */
export async function memoBeginnen(ort: NotizOrt, format: MemoFormat, jetzt = new Date()): Promise<string | null> {
  return speicher.transaction("rw", [speicher.notizen, speicher.memos, speicher.fotos, speicher.aufnahmen, speicher.nachtraege], async () => {
    const sendung = await sendungPruefen(ort.aufnahmeId);
    if (!(await fotoDerNotiz(ort, sendung))) return null;
    let n = await notizFinden(ort, sendung);
    if (!n) {
      n = { id: neueKennung(), aufnahmeId: ort.aufnahmeId, stationId: ort.stationId, fotoId: ort.fotoId, text: "", nachtragId: sendung };
      await speicher.notizen.add(n);
    }
    const id = neueKennung();
    await speicher.memos.add({
      id,
      aufnahmeId: ort.aufnahmeId,
      notizId: n.id,
      pruefsumme: null,
      format,
      dauerSekunden: 0,
      aufgenommenAm: jetzt.toISOString(),
      nachtragId: sendung,
      hochgeladen: false,
    });
    return id;
  });
}

/**
 * Ein Teilstück sofort sichern, mit der bis hierher gemessenen Dauer.
 * false: Das Memo gibt es nicht mehr (das Foto wurde mitten in der
 * Aufnahme gelöscht) oder es ist schon fertig – dann wird nichts gesichert.
 */
export async function memoTeilSichern(memoId: string, nr: number, teil: Blob, sekunden: number, jetzt = new Date()): Promise<boolean> {
  // Als ArrayBuffer, nicht als Blob (dateien.ts: warum) – vor der Transaktion gelesen.
  const daten = await teil.arrayBuffer();
  return speicher.transaction("rw", speicher.memos, speicher.memoTeile, async () => {
    const m = await speicher.memos.get(memoId);
    if (!m || m.pruefsumme !== null) return false;
    await speicher.memoTeile.add({ memoId, nr, teil: daten, zeit: jetzt.toISOString(), sekunden });
    return true;
  });
}

/**
 * Die Aufnahme ist zu Ende: Die Teilstücke werden zu einer Datei (mit dem
 * Format als Typ, abgelegt in „dateien“), die Prüfsumme steht fest, und in den Postausgang
 * kommen Notiz, Datei und Memo – in dieser Reihenfolge, damit am Server
 * nichts vor dem ankommt, woran es hängt. Die Notiz geht auch mit, wenn
 * sie leer ist: Am Server muss es sie geben, sonst hat das Memo keinen
 * Platz. `dauerSekunden`: am Gerät gemessen; ohne (nach einem Abbruch) die
 * Dauer, die mit dem letzten Teilstück gesichert wurde.
 *
 * Gibt das fertige Memo zurück – null, wenn nichts aufgenommen wurde oder
 * die Notiz nicht mehr da ist; dann ist das Memo am Gerät gelöscht.
 *
 * Ist seine Sendung inzwischen abgeschlossen – die Besichtigung (seit
 * Schritt 4) oder sein Nachtrag (seit Schritt 5) –, wird das Memo trotzdem
 * fertig – es bleibt am Gerät, geht aber nicht in den Postausgang: nach dem
 * Senden nähme der Server es nicht mehr an, und still verworfen wird nie,
 * was jemand eingesprochen hat. Vorkommen kann das kaum: `abschliessen` und
 * `nachtragSenden` warten, bis kein Memo mehr aufgenommen wird, und danach
 * beginnt keines mehr (`memoBeginnen`).
 */
export async function memoBeenden(memoId: string, dauerSekunden?: number): Promise<Memo | null> {
  // Zusammensetzen und Prüfsumme außerhalb der Transaktion – das Rechnen ist kein Speicherzugriff.
  const vorher = await speicher.memos.get(memoId);
  if (!vorher) return null;
  if (vorher.pruefsumme !== null) return vorher;
  const teile = await speicher.memoTeile.where("memoId").equals(memoId).sortBy("nr");
  const letztes = teile.at(-1);
  const dauer =
    dauerSekunden ?? letztes?.sekunden ?? (letztes ? (Date.parse(letztes.zeit) - Date.parse(vorher.aufgenommenAm)) / 1000 : 0);
  const daten = teile.length > 0 ? await new Blob(teile.map((t) => t.teil)).arrayBuffer() : null;
  const pruefsumme = daten && daten.byteLength > 0 ? await pruefsummeBerechnen(daten) : null;

  const tabellen = [speicher.memos, speicher.memoTeile, speicher.notizen, speicher.dateien, ...schreibTabellen()];
  return speicher.transaction("rw", tabellen, async () => {
    const m = await speicher.memos.get(memoId);
    if (!m || m.pruefsumme !== null) return m ?? null;
    const n = await speicher.notizen.get(m.notizId);
    if (!daten || !pruefsumme || !n) {
      await speicher.memoTeile.where("memoId").equals(memoId).delete();
      await speicher.memos.delete(memoId);
      return null;
    }
    await dateiAblegen(pruefsumme, daten, m.format);
    const fertig: Memo = { ...m, pruefsumme, dauerSekunden: dauerRunden(dauer) };
    await speicher.memos.put(fertig);
    await speicher.memoTeile.where("memoId").equals(memoId).delete();
    // Seine Sendung ist schon abgeschlossen: fertig und am Gerät, aber nicht mehr hinaus (siehe oben).
    const sendung = m.nachtragId ?? null;
    if (!(await sendungNochOffen(m.aufnahmeId, sendung))) return fertig;
    // War die Notiz schon zum Löschen vorgemerkt (Text geleert), gilt das nicht mehr: Sie trägt jetzt ein Memo.
    await austragen("geloescht", n.id);
    await vormerken("notiz", n.id, n.aufnahmeId, mitSendung(sendung));
    await vormerken("datei", pruefsumme, n.aufnahmeId);
    await vormerken("memo", memoId, n.aufnahmeId, mitSendung(sendung));
    return fertig;
  });
}

/**
 * Nimmt diese Sendung noch etwas an? Die erste, solange aufgenommen wird;
 * ein Nachtrag, solange er offen ist. Gibt es die Besichtigung nicht
 * (mehr), entscheidet der Aufrufer wie bisher – true.
 */
async function sendungNochOffen(aufnahmeId: string, sendung: string | null): Promise<boolean> {
  const a = await speicher.aufnahmen.get(aufnahmeId);
  if (!a) return true;
  if (sendung === null) return aenderbar(a);
  return a.stand === "im_buero" && (await speicher.nachtraege.get(sendung))?.stand === "offen";
}

/**
 * Beim Start der App: Memos, deren Aufnahme abbrach, werden aus ihren
 * Teilstücken fertig gemacht – so, als wäre „Stopp“ getippt worden.
 * `laeuftNoch` sagt, ob eine Aufnahme gerade wirklich noch läuft (in einem
 * anderen Fenster der App) – die bleibt, wie sie ist. Teilstücke ohne Memo
 * kommen weg. Gibt zurück, wie viele Memos gerettet wurden.
 */
export async function memosRetten(laeuftNoch: (memoId: string) => Promise<boolean> = () => Promise.resolve(false)): Promise<number> {
  let gerettet = 0;
  for (const m of await speicher.memos.filter((x) => x.pruefsumme === null).toArray()) {
    if (await laeuftNoch(m.id)) continue;
    if (await memoBeenden(m.id)) gerettet += 1;
  }
  // In einem Zug mit dem Lesen der Memos – eine gerade beginnende Aufnahme legt ihr Memo vor dem ersten Teilstück an.
  await speicher.transaction("rw", speicher.memos, speicher.memoTeile, async () => {
    const memoIds = new Set(await speicher.memos.toCollection().primaryKeys());
    await speicher.memoTeile.filter((t) => !memoIds.has(t.memoId)).delete();
  });
  return gerettet;
}

/**
 * Ein Memo am Gerät entfernen – mit allem, was dafür im Postausgang wartet
 * (Memo, Datei), und seiner Datei, wenn sie sonst niemand braucht. In einer
 * Transaktion mit Memos, Teilstücken, Fotos, Dateien und Postausgang.
 */
async function memoAmGeraetEntfernen(m: Memo): Promise<void> {
  await austragen("memo", m.id);
  await speicher.memoTeile.where("memoId").equals(m.id).delete();
  await speicher.memos.delete(m.id);
  if (m.pruefsumme !== null && (await speicher.memos.filter((x) => x.pruefsumme === m.pruefsumme).count()) === 0) {
    await austragen("datei", m.pruefsumme);
  }
  await dateiWegWennUnbenutzt(m.pruefsumme);
}

/**
 * Ein Sprachmemo löschen.
 *  - Ging es nie an den Server, verschwindet es nur am Gerät, mit allem,
 *    was dafür im Postausgang wartet.
 *  - Sonst geht „geloescht“ hinaus.
 * Bleibt die Notiz danach ohne Text und ohne Memo, gibt es sie nicht mehr –
 * wie beim Leeren des Textes geht für sie „geloescht“ hinaus. Im Nachtrag
 * lassen sich nur seine eigenen Memos löschen.
 */
export async function memoLoeschen(memo: Pick<Memo, "id">): Promise<void> {
  const tabellen = [speicher.memos, speicher.memoTeile, speicher.notizen, speicher.fotos, speicher.dateien, ...schreibTabellen()];
  await speicher.transaction("rw", tabellen, async () => {
    const m = await speicher.memos.get(memo.id);
    if (!m) return;
    const sendung = await sendungPruefen(m.aufnahmeId);
    nurAusDerSendung(m, sendung, MEMO_GESENDET);
    await memoAmGeraetEntfernen(m);
    if (m.abgeschickt) await vormerken("geloescht", m.id, m.aufnahmeId, { ...mitSendung(sendung), tabelle: "sprachmemo" });
    const n = await speicher.notizen.get(m.notizId);
    if (n && n.text.trim() === "" && (await speicher.memos.where("notizId").equals(n.id).count()) === 0) {
      await austragen("notiz", n.id);
      await vormerken("geloescht", n.id, n.aufnahmeId, { ...mitSendung(sendung), tabelle: "notiz" });
    }
  });
}

// --- Dachplanung mit K2 (seit E2, Schritt 3) ---------------------------------------
//
// Je Anlage eine Dachplanung, ihre Kennung ist die der Anlage: das PDF aus
// K2 (am Gerät als ArrayBuffer kopiert, nie das File-Objekt – Plan E2) oder
// „übersprungen“. Kein Pflichtschritt, und das PDF darf auch nach dem
// Senden noch kommen. Wie bei Fotos steht die Datei im Postausgang vor dem
// K2-Eintrag.
//
// Seit E3c (S5): dazu der Excel-Report von K2 (`K2.excel`) – ein eigener
// Platz, unabhängig vom PDF: „überspringen“ nimmt ihn nicht weg, ein neues
// PDF auch nicht. Der K2-Eintrag sagt, was sich geändert hat
// (`PostEintrag.k2Teile`) – reicht der Verkäufer nur die Liste nach, geht das
// PDF nicht noch einmal hinaus (sonst nähme es womöglich eines zurück, das
// das Büro inzwischen angehängt hat; Vertrag im Kern).

/** Ein geprüftes PDF oder ein geprüfter Excel-Report, fertig zum Ablegen (k2.ts). */
export interface PdfBereit {
  daten: ArrayBuffer;
  dateiname: string;
  pruefsumme: string;
}

/** Die Reihenfolge der Teile im K2-Eintrag. */
const K2_TEILE: readonly K2Teil[] = ["pdf", "excel"];

/**
 * Den Eintrag der Dachplanung vormerken – mit dem Teil, der sich geändert
 * hat, und dem, was schon wartete (ein Eintrag von vor dem Update, ohne
 * `k2Teile`, wartete mit dem PDF). Immer hinten: Sonst stünde ein wartender
 * K2-Eintrag vor seiner neuen Datei. In einer Transaktion mit `postausgangTabellen()`.
 */
async function k2Vormerken(anlageId: string, aufnahmeId: string, teil: K2Teil): Promise<void> {
  const da = await speicher.postausgang.where("[art+kennung]").equals(["k2", anlageId]).first();
  const teile = new Set<K2Teil>(da ? (da.k2Teile ?? ["pdf"]) : []);
  teile.add(teil);
  await hintenAnstellen("k2", anlageId, aufnahmeId, { k2Teile: K2_TEILE.filter((t) => teile.has(t)) });
}

/** Eine Dachplanung ohne PDF und ohne „übersprungen“ – der Platz, an dem zuerst nur die Liste liegt. */
function k2Leer(anlageId: string, aufnahmeId: string): K2 {
  return { id: anlageId, aufnahmeId, pdf: null, dateiname: null, groesse: 0, pruefsumme: null, uebersprungen: false, nachtragId: null, hochgeladen: false, excel: null };
}

/** Das K2-PDF ablegen – es ersetzt ein früheres PDF oder „übersprungen“; der Excel-Report bleibt. */
export async function k2PdfAblegen(aufnahme: Pick<Aufnahme, "id" | "anlage">, pdf: PdfBereit): Promise<void> {
  const anlageId = aufnahme.anlage.id;
  await speicher.transaction("rw", [speicher.k2, ...postausgangTabellen()], async () => {
    const vorher = await speicher.k2.get(anlageId);
    const k2: K2 = {
      id: anlageId,
      aufnahmeId: aufnahme.id,
      pdf: pdf.daten,
      dateiname: pdf.dateiname,
      groesse: pdf.daten.byteLength,
      pruefsumme: pdf.pruefsumme,
      uebersprungen: false,
      nachtragId: null,
      hochgeladen: vorher?.pruefsumme === pdf.pruefsumme ? vorher.hochgeladen : false,
      excel: vorher?.excel ?? null,
    };
    await speicher.k2.put(k2);
    if (vorher?.pruefsumme && vorher.pruefsumme !== pdf.pruefsumme) await austragen("datei", vorher.pruefsumme);
    // Dasselbe PDF noch einmal: nichts Neues zu senden.
    if (vorher?.pruefsumme === pdf.pruefsumme && !vorher.uebersprungen) return;
    await vormerken("datei", pdf.pruefsumme, aufnahme.id);
    await k2Vormerken(anlageId, aufnahme.id, "pdf");
  });
}

/** „Überspringen“ – auch, um ein abgelegtes PDF wieder wegzunehmen. Ein PDF kann jederzeit nachkommen; der Excel-Report bleibt. */
export async function k2Ueberspringen(aufnahme: Pick<Aufnahme, "id" | "anlage">): Promise<void> {
  const anlageId = aufnahme.anlage.id;
  await speicher.transaction("rw", [speicher.k2, ...postausgangTabellen()], async () => {
    const vorher = await speicher.k2.get(anlageId);
    if (vorher?.uebersprungen) return;
    await speicher.k2.put({ ...k2Leer(anlageId, aufnahme.id), uebersprungen: true, excel: vorher?.excel ?? null });
    if (vorher?.pruefsumme) await austragen("datei", vorher.pruefsumme);
    await k2Vormerken(anlageId, aufnahme.id, "pdf");
  });
}

/**
 * Den Excel-Report von K2 ablegen (seit E3c, S5; ANFORDERUNGEN.md H24) – er
 * ersetzt einen früheren; das PDF bleibt, wie es ist. Wie das PDF jederzeit,
 * auch nach dem Senden. Entfernen gibt es nicht, nur ersetzen (Vertrag).
 */
export async function k2ExcelAblegen(aufnahme: Pick<Aufnahme, "id" | "anlage">, excel: PdfBereit): Promise<void> {
  const anlageId = aufnahme.anlage.id;
  await speicher.transaction("rw", [speicher.k2, ...postausgangTabellen()], async () => {
    const vorher = (await speicher.k2.get(anlageId)) ?? k2Leer(anlageId, aufnahme.id);
    const alt = vorher.excel ?? null;
    const neu: K2Excel = {
      daten: excel.daten,
      dateiname: excel.dateiname,
      groesse: excel.daten.byteLength,
      pruefsumme: excel.pruefsumme,
      hochgeladen: alt?.pruefsumme === excel.pruefsumme ? alt.hochgeladen : false,
    };
    await speicher.k2.put({ ...vorher, excel: neu });
    // Dieselbe Liste noch einmal: nichts Neues zu senden.
    if (alt?.pruefsumme === excel.pruefsumme) return;
    if (alt) await austragen("datei", alt.pruefsumme);
    await vormerken("datei", excel.pruefsumme, aufnahme.id);
    await k2Vormerken(anlageId, aufnahme.id, "excel");
  });
}

/**
 * Eine Länge (Kabelkanal, Rohr, Kabel) – eine je Art und Station, ihre
 * Kennung entsteht beim ersten Wert und bleibt. Leer (null) heißt „keine
 * Angabe“: Am Server wird sie dann gelöscht.
 *
 * Im Nachtrag (seit Schritt 5), je nachdem, was es schon gibt:
 *  - keine Länge dieser Art, oder eine, die gesendet geleert war (am Server
 *    also nicht da): Sie kommt mit dem Nachtrag neu dazu – leeren geht wie
 *    beim Aufnehmen;
 *  - eine gesendete Länge: Sie wird berichtigt (`laengeBerichtigen`).
 */
export async function laengeSpeichern(station: Pick<Station, "id" | "aufnahmeId">, laengenArt: string, meter: number | null): Promise<void> {
  await speicher.transaction("rw", [speicher.laengen, ...schreibTabellen()], async () => {
    const sendung = await sendungPruefen(station.aufnahmeId);
    let l = await speicher.laengen.where("[stationId+laengenArt]").equals([station.id, laengenArt]).first();
    if (l && sendung !== null && (l.nachtragId ?? null) !== sendung) {
      if (l.meter !== null) return laengeBerichtigen(l, meter, sendung);
      if (meter === null) return;
      // Gesendet geleert: Mit diesem Nachtrag kommt sie neu dazu – unter derselben Kennung, die am Server frei ist.
      await speicher.laengen.update(l.id, { meter, nachtragId: sendung, geaendertImNachtrag: null });
      await austragen("geloescht", l.id);
      await vormerken("laenge", l.id, station.aufnahmeId, mitSendung(sendung));
      return;
    }
    if (!l) {
      if (meter === null) return;
      l = {
        id: neueKennung(),
        aufnahmeId: station.aufnahmeId,
        stationId: station.id,
        laengenArt,
        meter,
        anmerkung: null,
        nachtragId: sendung,
        geaendertImNachtrag: null,
      };
      await speicher.laengen.add(l);
    } else {
      if (l.meter === meter) return;
      await speicher.laengen.update(l.id, { meter });
    }
    if (meter === null) {
      await austragen("laenge", l.id);
      await vormerken("geloescht", l.id, station.aufnahmeId, { ...mitSendung(sendung), tabelle: "laenge" });
    } else {
      await austragen("geloescht", l.id);
      await vormerken("laenge", l.id, station.aufnahmeId, mitSendung(sendung));
    }
  });
}

/**
 * Eine gesendete Länge im Nachtrag berichtigen: neuer Wert, markiert mit
 * dem Nachtrag (`geaendertImNachtrag`); sie gehört weiter zu ihrer Sendung.
 * Leeren geht nicht (`LeerImNachtrag`) – am Server ließe sie sich nicht
 * löschen, was gesendet ist, bleibt. Derselbe Wert ändert nichts. In der
 * Transaktion von `laengeSpeichern`.
 */
async function laengeBerichtigen(l: Laenge, meter: number | null, nachtragId: string): Promise<void> {
  if (meter === null) throw new LeerImNachtrag();
  if (l.meter === meter) return;
  await speicher.laengen.update(l.id, { meter, geaendertImNachtrag: nachtragId });
  await vormerken("laenge", l.id, l.aufnahmeId, { nachtragId });
}

// --- Material von draußen (seit E3b, O65) --------------------------------------------

const MATERIAL_GESENDET = "Dieses Material ist schon im Büro – die Menge lässt sich berichtigen, entfernen nicht (0 eintragen).";

/** Was ein neues Material trägt – aus der Standardliste oder „Etwas anderes“. */
export interface NeuesMaterial {
  /** Die Station, aus der es kommt – null: auf der Seite „Material“. */
  stationId: string | null;
  /** Der Eintrag der Standardliste – null: „Etwas anderes“. */
  standardId: string | null;
  artikelnummer: string | null;
  bezeichnung: string;
  menge: number;
  einheit: string;
}

/**
 * Material dazu – mit der Sendung, die gerade läuft (beim Aufnehmen die
 * erste, sonst der offene Nachtrag). Gibt die Kennung zurück.
 */
export async function materialDazu(aufnahmeId: string, neu: NeuesMaterial): Promise<string> {
  const id = neueKennung();
  await speicher.transaction("rw", [speicher.materialangaben, ...schreibTabellen()], async () => {
    const sendung = await sendungPruefen(aufnahmeId);
    const vorhanden = await speicher.materialangaben.where("aufnahmeId").equals(aufnahmeId).toArray();
    const m: Materialangabe = {
      id,
      aufnahmeId,
      stationId: neu.stationId,
      standardId: neu.standardId,
      artikelnummer: neu.artikelnummer?.trim() || null,
      bezeichnung: neu.bezeichnung.trim(),
      menge: neu.menge,
      einheit: neu.einheit.trim(),
      nachtragId: sendung,
      geaendertImNachtrag: null,
      nr: vorhanden.reduce((hoechste, x) => Math.max(hoechste, x.nr + 1), 0),
    };
    await speicher.materialangaben.add(m);
    await vormerken("material", id, aufnahmeId, mitSendung(sendung));
  });
  return id;
}

/**
 * Die Menge ändern. Beim Aufnehmen und im eigenen Nachtrag: einfach ändern.
 * Im Nachtrag an Material, das schon im Büro ist: berichtigen – markiert mit
 * dem Nachtrag (`geaendertImNachtrag`), es gehört weiter zu seiner Sendung.
 * Dieselbe Menge noch einmal ändert nichts.
 */
export async function materialMenge(id: string, menge: number): Promise<void> {
  await speicher.transaction("rw", [speicher.materialangaben, ...schreibTabellen()], async () => {
    const m = await speicher.materialangaben.get(id);
    if (!m) return;
    const sendung = await sendungPruefen(m.aufnahmeId);
    if (sendung !== null && (m.nachtragId ?? null) !== sendung) {
      // Derselbe Wert ist keine Berichtigung (wie bei der Länge) – sonst stünde „berichtigt“ schon beim Verlassen des Feldes da.
      if (m.menge === menge) return;
      await speicher.materialangaben.update(id, { menge, geaendertImNachtrag: sendung });
      await vormerken("material", id, m.aufnahmeId, { nachtragId: sendung });
      return;
    }
    if (m.menge === menge) return;
    await speicher.materialangaben.update(id, { menge });
    await vormerken("material", id, m.aufnahmeId, mitSendung(sendung));
  });
}

/**
 * Material entfernen – nur, was in derselben Sendung dazukam (im Nachtrag
 * also nichts, was schon im Büro ist: `SchonGesendet`). Am Gerät ist es weg;
 * „geloescht“ geht hinaus – kam es nie an, ist das am Server kein Fehler.
 */
export async function materialEntfernen(id: string): Promise<void> {
  await speicher.transaction("rw", [speicher.materialangaben, ...schreibTabellen()], async () => {
    const m = await speicher.materialangaben.get(id);
    if (!m) return;
    const sendung = await sendungPruefen(m.aufnahmeId);
    nurAusDerSendung(m, sendung, MATERIAL_GESENDET);
    await speicher.materialangaben.delete(id);
    await austragen("material", id);
    await vormerken("geloescht", id, m.aufnahmeId, { ...mitSendung(sendung), tabelle: "materialangabe" });
  });
}

/**
 * Eine eigene Angabe („Etwas anderes“) ändern – Bezeichnung, Einheit, Menge
 * (Gernot, Augentest E3b, 23.10.2026: ein Tippfehler ließ sich nur mit ✕ und
 * neu beheben). Erlaubt, wo sich die Angabe auch entfernen ließe
 * (`SchonGesendet` sonst), nie bei der Standardliste – dort kommt der Name
 * vom Büro.
 *
 * Unter derselben Kennung, an ihrer Stelle: Der Server nimmt bei eigenen
 * Angaben aus derselben Sendung auch Bezeichnung und Einheit an
 * (server/src/doku/eintraege.ts, `aenderbar`), im Verlauf steht dann
 * „geändert“. Bis zur Nachprüfung E3b (23.10.2026, N1) wurde die Angabe hier
 * ersetzt (alte weg, neue dazu) – mit der falschen Begründung, der Server
 * ändere nur die Menge. Ist nichts anders, geht nichts hinaus. Gibt die
 * Kennung zurück.
 */
export async function materialAendern(id: string, a: Pick<NeuesMaterial, "bezeichnung" | "einheit" | "menge">): Promise<string | null> {
  let danach: string | null = null;
  await speicher.transaction("rw", [speicher.materialangaben, ...schreibTabellen()], async () => {
    const m = await speicher.materialangaben.get(id);
    if (!m) return;
    const sendung = await sendungPruefen(m.aufnahmeId);
    nurAusDerSendung(m, sendung, MATERIAL_GESENDET);
    if (m.standardId !== null) throw new Error("Material aus der Standardliste – der Name kommt vom Büro.");
    danach = m.id;
    const neu = { bezeichnung: a.bezeichnung.trim(), einheit: a.einheit.trim(), menge: a.menge };
    if (neu.bezeichnung === m.bezeichnung && neu.einheit === m.einheit && neu.menge === m.menge) return;
    await speicher.materialangaben.update(m.id, neu);
    await vormerken("material", m.id, m.aufnahmeId, mitSendung(sendung));
  });
  return danach;
}

/**
 * Liest eine Länge, wie Menschen sie tippen: „12,5“ oder „12.5“, auch mit
 * „m“ dahinter. Leer: keine Angabe (null). Ungültig: undefined.
 */
export function meterLesen(eingabe: string): number | null | undefined {
  const roh = eingabe.trim().replace(/\s*m$/i, "").replace(",", ".");
  if (roh === "") return null;
  if (!/^\d{1,4}(\.\d{0,2})?$/.test(roh)) return undefined;
  const zahl = Number(roh);
  return Number.isFinite(zahl) && zahl >= 0 && zahl <= 9999 ? zahl : undefined;
}

/** Eine Länge zum Anzeigen: 12.5 → „12,5“. */
export function meterZeigen(meter: number | null | undefined): string {
  return meter === null || meter === undefined ? "" : String(meter).replace(".", ",");
}

// --- Abschließen und senden (seit E2, Schritt 4) -------------------------------------
//
// „An das Büro senden“ geht erst, wenn alle sieben Stationen fertig sind
// (Entscheidung Gernots, 19.10.2026 – ein Vorschlag, billig umkehrbar). Die
// Dachplanung mit K2 ist keine Bedingung, sie darf nachkommen. Der
// Abschluss steht hinten im Postausgang: Er geht erst, wenn alles davor am
// Server ist, und der Server bestätigt ihn erst, wenn dort alles da ist.

/**
 * Was „An das Büro senden“ ergab:
 *  - abgeschlossen: Die Besichtigung ist zu und wartet im Postausgang.
 *  - nicht-fertig: Es ist noch nicht jede Station fertig – und es wurde
 *    nicht ausdrücklich „trotzdem senden“ gesagt.
 *  - memo-offen: Ein Sprachmemo wird gerade aufgenommen (oder nach einem
 *    Abbruch noch nicht gerettet) – es soll mit.
 *  - gesperrt: Die Besichtigung ist schon abgeschlossen oder fehlt.
 * In allen Fällen außer dem ersten ändert sich nichts.
 */
export type AbschlussErgebnis = "abgeschlossen" | "nicht-fertig" | "memo-offen" | "gesperrt";

/**
 * „An das Büro senden“: abschließen und den Abschluss hinten in den
 * Postausgang stellen. Ist nicht jede Station fertig, geht das nur mit
 * `auchUnfertig` – nach der Rückfrage „Trotzdem senden?“ (Gernot,
 * 19.10.2026: Kommt man an eine Station nicht hin, muss Senden trotzdem
 * gehen). Die Station bleibt dann „nicht fertig“, so sieht es das Büro.
 */
export async function abschliessen(aufnahmeId: string, jetzt = new Date(), { auchUnfertig = false } = {}): Promise<AbschlussErgebnis> {
  return speicher.transaction("rw", [speicher.stationen, speicher.memos, ...postausgangTabellen()], async () => {
    const a = await speicher.aufnahmen.get(aufnahmeId);
    if (!a || !aenderbar(a)) return "gesperrt";
    const stationen = await speicher.stationen.where("aufnahmeId").equals(aufnahmeId).toArray();
    if (!auchUnfertig && (stationen.length < STATIONEN.length || stationen.some((s) => !s.fertig))) return "nicht-fertig";
    if ((await speicher.memos.where("aufnahmeId").equals(aufnahmeId).filter((m) => m.pruefsumme === null).count()) > 0) return "memo-offen";
    await speicher.aufnahmen.update(aufnahmeId, { stand: "abgeschlossen", abgeschlossenAm: jetzt.toISOString() });
    // Hinten – auch wenn (wider Erwarten) schon ein Abschluss wartete: Er muss nach allem anderen kommen.
    await hintenAnstellen("abgeschlossen", aufnahmeId, aufnahmeId);
    return "abgeschlossen";
  });
}

/**
 * Der Server hat den Abschluss bestätigt: alles da, die Besichtigung liegt
 * im Büro unter „Zu tun“. Ein Fehler an ihr stammt danach nur noch von
 * dem, was noch wartet (etwa ein K2-PDF nach dem Senden).
 */
export async function aufnahmeImBuero(aufnahmeId: string, jetzt = new Date()): Promise<void> {
  await speicher.transaction("rw", [...postausgangTabellen()], async () => {
    const a = await speicher.aufnahmen.get(aufnahmeId);
    if (!a || a.stand === "im_buero") return;
    await speicher.aufnahmen.update(aufnahmeId, { stand: "im_buero", imBueroAm: jetzt.toISOString(), fehler: null });
    await aufnahmeFehlerAktualisieren(aufnahmeId);
  });
}

// --- Etwas nachreichen (seit E2, Schritt 5) --------------------------------------------
//
// Gernot, 19.10.2026: Wer nach dem Senden etwas vergessen hat, reicht es
// nach – eine zweite Besichtigung dafür „wäre nur verwirrend“. Ein Nachtrag
// ist eine eigene Sendung derselben Besichtigung: „Etwas nachreichen“
// öffnet ihn (Eintrag „nachtrag“), was darin entsteht oder berichtigt wird,
// trägt seine Kennung (oben, je Funktion), und „Nachtrag senden“ stellt
// seinen Abschluss hinten in den Postausgang – mit den Kennungen genau
// dieses Nachtrags, derselbe Abgleich wie beim ersten Senden. Je
// Besichtigung läuft höchstens einer; der nächste beginnt erst, wenn der
// Server den letzten bestätigt hat. Die Besichtigung selbst bleibt „im
// Büro“.

/**
 * Was „Etwas nachreichen“ ergab:
 *  - offen: Der Nachtrag läuft – neu angelegt, oder es lief schon einer
 *    (dann dieser; zweimal getippt legt keinen zweiten an).
 *  - unterwegs: Der letzte Nachtrag ist noch auf dem Weg ins Büro – erst
 *    danach ein neuer (der Server nimmt nur einen offenen an).
 *  - gesperrt: Die Besichtigung ist (noch) nicht im Büro – oder fehlt.
 */
export type NachtragBeginn = { art: "offen"; nachtragId: string } | { art: "unterwegs" } | { art: "gesperrt" };

/** „Etwas nachreichen“: den Nachtrag anlegen und vormerken – nur an einer Besichtigung, die im Büro ist. */
export async function nachtragBeginnen(aufnahmeId: string, jetzt = new Date()): Promise<NachtragBeginn> {
  return speicher.transaction("rw", [...schreibTabellen()], async () => {
    const a = await speicher.aufnahmen.get(aufnahmeId);
    if (a?.stand !== "im_buero") return { art: "gesperrt" };
    const nachtraege = await speicher.nachtraege.where("aufnahmeId").equals(aufnahmeId).toArray();
    const offen = offenerNachtrag(nachtraege);
    if (offen) return { art: "offen", nachtragId: offen.id };
    if (nachtragUnterwegs(nachtraege)) return { art: "unterwegs" };
    const n: Nachtrag = { id: neueKennung(), aufnahmeId, begonnenAm: jetzt.toISOString(), stand: "offen", abgeschlossenAm: null, imBueroAm: null };
    await speicher.nachtraege.add(n);
    await vormerken("nachtrag", n.id, aufnahmeId, { nachtragId: n.id });
    return { art: "offen", nachtragId: n.id };
  });
}

/**
 * Was „Nachtrag senden“ ergab:
 *  - gesendet: Er ist zu und wartet im Postausgang.
 *  - leer: Es ist noch nichts darin – dann eher „Nachtrag abbrechen“.
 *  - memo-offen: Ein Sprachmemo für ihn wird gerade aufgenommen – es soll mit.
 *  - gesperrt: Er läuft nicht (mehr) – schon gesendet oder unbekannt.
 * In allen Fällen außer dem ersten ändert sich nichts.
 */
export type NachtragSendenErgebnis = "gesendet" | "leer" | "memo-offen" | "gesperrt";

/** Die Tabellen für „Nachtrag senden“ und „abbrechen“: alles, was ein Nachtrag gebracht haben kann. */
function nachtragTabellen() {
  return [...stueckeTabellen(), ...schreibTabellen()];
}

/** „Nachtrag senden“: den Nachtrag schließen und seinen Abschluss hinten in den Postausgang stellen. */
export async function nachtragSenden(nachtragId: string, jetzt = new Date()): Promise<NachtragSendenErgebnis> {
  return speicher.transaction("rw", nachtragTabellen(), async () => {
    const n = await speicher.nachtraege.get(nachtragId);
    if (n?.stand !== "offen") return "gesperrt";
    const stuecke = await stueckeLesen(n.aufnahmeId);
    if (stuecke.memos.some((m) => m.nachtragId === nachtragId && m.pruefsumme === null)) return "memo-offen";
    if (nachtragLeer(stuecke, nachtragId)) return "leer";
    await nachtragSchliessen(n, jetzt);
    return "gesendet";
  });
}

/** Zu und hinten in den Postausgang – nach allem, was er gebracht hat. In einer Transaktion mit `schreibTabellen()`. */
async function nachtragSchliessen(n: Nachtrag, jetzt: Date): Promise<void> {
  await speicher.nachtraege.update(n.id, { stand: "abgeschlossen", abgeschlossenAm: jetzt.toISOString() });
  await hintenAnstellen("nachtrag-abgeschlossen", n.id, n.aufnahmeId, { nachtragId: n.id });
}

/**
 * Was „Nachtrag abbrechen“ ergab:
 *  - verworfen: Er war nie am Server – am Gerät ist er weg, samt dem, was
 *    für ihn im Postausgang wartete.
 *  - geschlossen: Er ist (vielleicht) am Server offen – dort wird er leer
 *    geschlossen, wie „Nachtrag senden“; das Büro blendet einen leeren aus.
 *  - nicht-leer: Es ist schon etwas darin – abbrechen ginge nicht ohne
 *    Verlust; dann wird gesendet (oder erst wieder herausgenommen).
 *  - gesperrt: Er läuft nicht (mehr).
 */
export type NachtragVerwerfenErgebnis = "verworfen" | "geschlossen" | "nicht-leer" | "gesperrt";

/**
 * „Nachtrag abbrechen“ – nur, solange er leer ist. Ob er am Server ist,
 * sagt `abgeschickt` (der Sender setzt es, bevor „nachtrag“ hinausgeht):
 * Nie hinausgegangen – oder vom Server abgewiesen (dauerhafter Fehler am
 * Eintrag) –, ist er dort sicher nicht und verschwindet nur am Gerät.
 * Sonst bliebe er am Server offen, und kein neuer könnte beginnen; deshalb
 * schließt die App ihn dort mit leeren Listen.
 */
export async function nachtragVerwerfen(nachtragId: string, jetzt = new Date()): Promise<NachtragVerwerfenErgebnis> {
  return speicher.transaction("rw", nachtragTabellen(), async () => {
    const n = await speicher.nachtraege.get(nachtragId);
    if (n?.stand !== "offen") return "gesperrt";
    if (!nachtragLeer(await stueckeLesen(n.aufnahmeId), nachtragId)) return "nicht-leer";
    const beginn = await speicher.postausgang.where("[art+kennung]").equals(["nachtrag", nachtragId]).first();
    const vielleichtAmServer = n.abgeschickt === true && !(beginn && beginn.fehler !== null);
    if (vielleichtAmServer) {
      await nachtragSchliessen(n, jetzt);
      return "geschlossen";
    }
    // Nur am Gerät: was für ihn wartet, und was von ihm übrig ist (geleerte Notizen und Längen).
    await speicher.postausgang
      .where("aufnahmeId")
      .equals(n.aufnahmeId)
      .filter((e) => e.nachtragId === nachtragId)
      .delete();
    await speicher.notizen
      .where("aufnahmeId")
      .equals(n.aufnahmeId)
      .filter((x) => x.nachtragId === nachtragId)
      .delete();
    await speicher.laengen
      .where("aufnahmeId")
      .equals(n.aufnahmeId)
      .filter((x) => x.nachtragId === nachtragId)
      .delete();
    await speicher.materialangaben
      .where("aufnahmeId")
      .equals(n.aufnahmeId)
      .filter((x) => x.nachtragId === nachtragId)
      .delete();
    await speicher.nachtraege.delete(nachtragId);
    await aufnahmeFehlerAktualisieren(n.aufnahmeId);
    return "verworfen";
  });
}

/**
 * Der Server hat den Nachtrag bestätigt: alles da, er ist im Büro – als
 * neu erkennbar. Danach kann der nächste beginnen. Ein Fehler an der
 * Besichtigung stammt nur noch von dem, was noch wartet.
 */
export async function nachtragImBuero(nachtragId: string, jetzt = new Date()): Promise<void> {
  await speicher.transaction("rw", [...schreibTabellen()], async () => {
    const n = await speicher.nachtraege.get(nachtragId);
    if (!n || n.stand === "im_buero") return;
    await speicher.nachtraege.update(nachtragId, { stand: "im_buero", imBueroAm: jetzt.toISOString() });
    await aufnahmeFehlerAktualisieren(n.aufnahmeId);
  });
}
